import React from 'react';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Edit,
  Archive,
  ArchiveRestore,
  FileText,
  FileSpreadsheet,
  Files,
  ListOrdered,
  Lock,
} from 'lucide-react';
import POStatusSelector from '@/components/admin/POStatusSelector';
import { calendarDate } from '@/lib/dateOnly';
import {
  PO_TABLE_COLUMNS,
  POTableColumn,
  canResellerEditPurchaseOrder,
  getResellerPOEditBlockedMessage,
} from './purchaseOrderTableUtils';
import {
  getPoArchiveDisabledMessage,
  isPoArchiveDisabled,
} from './purchaseOrderArchiveUtils';

const statusColors: Record<string, string> = {
  pending_upload: 'bg-gray-100 text-gray-800',
  uploaded: 'bg-blue-100 text-blue-800',
  under_review: 'bg-yellow-100 text-yellow-800',
  funded: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  archived: 'bg-gray-200 text-gray-500',
  pending: 'bg-blue-100 text-blue-800',
};

const statusLabels: Record<string, string> = {
  pending_upload: 'Pending Upload',
  uploaded: 'Uploaded',
  under_review: 'Under Review',
  funded: 'Funded',
  rejected: 'Rejected',
  archived: 'Archived',
  pending: 'Uploaded',
};

const ARCHIVABLE_STATUSES = ['pending_upload', 'uploaded', 'rejected', 'pending'];

const normalizeStatus = (status: string): string => {
  if (status === 'pending') return 'uploaded';
  return status;
};

const getFileIcon = (fileName: string | null) => {
  if (!fileName) return null;
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return <FileText className="h-5 w-5 flex-shrink-0 text-destructive" />;
  if (['xls', 'xlsx', 'csv'].includes(ext || '')) {
    return <FileSpreadsheet className="h-5 w-5 flex-shrink-0 text-primary" />;
  }
  return <FileText className="h-5 w-5 flex-shrink-0 text-muted-foreground" />;
};

export interface PurchaseOrderFileForTable {
  id: string;
  file_name: string;
  storage_path: string;
  manufacturer_name: string | null;
}

export interface PurchaseOrderForTable {
  id: string;
  po_number: string;
  vendor_name: string;
  status: string;
  purchase_date: string | null;
  created_at: string;
  created_by_email: string | null;
  manufacturer_name: string | null;
  uploaded_by_email: string | null;
  file_uploaded_at: string | null;
  file_name: string | null;
  storage_path: string | null;
  files?: PurchaseOrderFileForTable[];
  archive_disabled: boolean;
  is_immutable: boolean;
  immutable_at: string | null;
  immutable_reason: string | null;
  archived_at: string | null;
  archived_by_email: string | null;
  archive_reason: string | null;
  _isSourceOrgPO?: boolean;
}

export interface PurchaseOrdersTableProps {
  purchaseOrders: PurchaseOrderForTable[];
  isAdmin: boolean;
  isResellerPortal: boolean;
  sortColumn: POTableColumn | null;
  sortDirection: 'asc' | 'desc';
  onSort: (column: POTableColumn) => void;
  renderSortIndicator: (column: POTableColumn) => React.ReactNode;
  onViewFile: (poId: string, storagePath: string, fileName: string) => void;
  onOpenDialog: (po: PurchaseOrderForTable) => void;
  onArchiveClick: (po: PurchaseOrderForTable) => void;
  onUnarchive: (po: PurchaseOrderForTable) => void;
  onViewLineItems: (poId: string) => void;
  onStatusChange: () => void;
}

const PurchaseOrdersTable: React.FC<PurchaseOrdersTableProps> = ({
  purchaseOrders,
  isAdmin,
  isResellerPortal,
  sortColumn,
  sortDirection: _sortDirection,
  onSort,
  renderSortIndicator,
  onViewFile,
  onOpenDialog,
  onArchiveClick,
  onUnarchive,
  onViewLineItems,
  onStatusChange,
}) => {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {PO_TABLE_COLUMNS.map((column) => (
            <TableHead
              key={column}
              className="cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => onSort(column)}
            >
              <div className="flex items-center gap-1 font-semibold">
                {column}
                {renderSortIndicator(column)}
              </div>
            </TableHead>
          ))}
          <TableHead className="w-[120px]">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {purchaseOrders.map((po) => {
          const fileCount = (po.files?.length || 0) + (po.file_name ? 1 : 0);
          const primaryFile =
            po.files?.[0] ||
            (po.file_name
              ? {
                  file_name: po.file_name,
                  storage_path: po.storage_path,
                  manufacturer_name: po.manufacturer_name,
                }
              : null);

          const normalizedStatus = normalizeStatus(po.status);
          const archiveDisabled = isPoArchiveDisabled(po.archive_disabled);
          const canArchive =
            ARCHIVABLE_STATUSES.includes(po.status) &&
            !po.is_immutable &&
            !po._isSourceOrgPO &&
            !archiveDisabled;
          const isSourcePO = !!po._isSourceOrgPO;
          const canResellerEdit =
            !isResellerPortal || canResellerEditPurchaseOrder(po.status);

          return (
            <TableRow
              key={po.id}
              data-testid="purchase-order-row"
              className={isSourcePO ? 'opacity-75' : ''}
            >
              <TableCell>
                {primaryFile ? (
                  <div className="space-y-1">
                    <div className="flex items-start gap-2">
                      {getFileIcon(primaryFile.file_name)}
                      <button
                        onClick={() =>
                          primaryFile.storage_path &&
                          onViewFile(po.id, primaryFile.storage_path, primaryFile.file_name)
                        }
                        className="text-sm text-primary hover:text-primary/80 hover:underline break-all text-left"
                        title={`View ${primaryFile.file_name}`}
                      >
                        {primaryFile.file_name}
                      </button>
                    </div>
                    {fileCount > 1 && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Files className="h-3 w-3" />
                        <span>+{fileCount - 1} more file(s)</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>
                {(() => {
                  const fileManufacturers = (po.files || [])
                    .map((f) => f.manufacturer_name)
                    .filter((m): m is string => !!m);
                  const uniqueManufacturers = [...new Set(fileManufacturers)];

                  if (uniqueManufacturers.length === 0) {
                    return po.manufacturer_name || po.vendor_name || '-';
                  }
                  if (uniqueManufacturers.length === 1) {
                    return uniqueManufacturers[0];
                  }
                  return (
                    <div className="space-y-0.5">
                      <span>{uniqueManufacturers[0]}</span>
                      <div className="text-xs text-muted-foreground">
                        +{uniqueManufacturers.length - 1} more
                      </div>
                    </div>
                  );
                })()}
              </TableCell>
              <TableCell>
                {po.purchase_date
                  ? format(calendarDate(po.purchase_date), 'MMM d, yyyy')
                  : po.file_uploaded_at
                    ? format(new Date(po.file_uploaded_at), 'MMM d, yyyy')
                    : format(new Date(po.created_at), 'MMM d, yyyy')}
              </TableCell>
              <TableCell>
                {po.file_uploaded_at
                  ? format(new Date(po.file_uploaded_at), 'MMM d, yyyy, h:mm a')
                  : format(new Date(po.created_at), 'MMM d, yyyy, h:mm a')}
              </TableCell>
              <TableCell>
                <span className="text-sm text-muted-foreground">
                  {po.created_by_email || po.uploaded_by_email || '-'}
                </span>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {po.is_immutable && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Lock className="h-4 w-4 text-amber-500" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Locked ({po.immutable_reason})</p>
                          {po.immutable_at && (
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(po.immutable_at), 'MMM d, yyyy HH:mm')}
                            </p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {isAdmin &&
                  po.status !== 'pending_upload' &&
                  po.status !== 'archived' &&
                  !po.is_immutable ? (
                    <POStatusSelector
                      poId={po.id}
                      poNumber={po.po_number}
                      currentStatus={normalizedStatus}
                      onStatusChange={onStatusChange}
                    />
                  ) : po.status === 'archived' && po.archived_at ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Badge className={statusColors[po.status] || statusColors.uploaded}>
                            {statusLabels[po.status] || normalizedStatus.replace(/_/g, ' ')}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="space-y-1">
                            <p className="font-medium">Archived</p>
                            {po.archived_by_email && (
                              <p className="text-xs">By: {po.archived_by_email}</p>
                            )}
                            <p className="text-xs">
                              {format(new Date(po.archived_at), 'MMM d, yyyy HH:mm')}
                            </p>
                            {po.archive_reason && (
                              <p className="text-xs text-muted-foreground">
                                Reason: {po.archive_reason}
                              </p>
                            )}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <Badge className={statusColors[po.status] || statusColors.uploaded}>
                      {statusLabels[po.status] || normalizedStatus.replace(/_/g, ' ')}
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                {isSourcePO ? (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Lock size={14} />
                    <span>Source Org</span>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    {canResellerEdit ? (
                      <button
                        onClick={() => onOpenDialog(po)}
                        className="text-primary hover:text-primary/80"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                    ) : (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="text-muted-foreground/40 cursor-not-allowed inline-flex"
                              data-testid="edit-po-disabled"
                              aria-disabled="true"
                            >
                              <Edit size={16} />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{getResellerPOEditBlockedMessage(po.status)}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {!isResellerPortal &&
                      (normalizedStatus !== 'archived' ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => onViewLineItems(po.id)}
                                className="text-muted-foreground hover:text-foreground"
                                title="View Line Items"
                              >
                                <ListOrdered size={16} />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>View Line Items</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-muted-foreground/40 cursor-not-allowed">
                                <ListOrdered size={16} />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Cannot view line items for archived POs</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ))}
                    {normalizedStatus === 'archived' ? (
                      <button
                        onClick={() => onUnarchive(po)}
                        className="text-muted-foreground hover:text-foreground"
                        title="Unarchive"
                      >
                        <ArchiveRestore size={16} />
                      </button>
                    ) : archiveDisabled ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="inline-flex items-center gap-1 text-amber-600 cursor-not-allowed"
                              data-testid="archive-disabled"
                              aria-disabled="true"
                            >
                              <Lock size={14} aria-hidden="true" />
                              <span className="text-xs font-medium">Locked</span>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{getPoArchiveDisabledMessage()}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : canArchive ? (
                      <button
                        onClick={() => onArchiveClick(po)}
                        className="text-muted-foreground hover:text-foreground"
                        title="Archive"
                        data-testid="archive-enabled"
                      >
                        <Archive size={16} />
                      </button>
                    ) : (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-muted-foreground/40 cursor-not-allowed">
                              <Archive size={16} />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              {po.is_immutable
                                ? `Cannot archive: PO is locked (${po.immutable_reason})`
                                : `Cannot archive: PO is ${statusLabels[po.status] || normalizedStatus.replace(/_/g, ' ')}`}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default PurchaseOrdersTable;
